import {
  type CSSProperties,
  type KeyboardEvent,
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  useDndMonitor,
  useDraggable,
  useDroppable,
} from '@dnd-kit/core';
import { CSS } from '@dnd-kit/utilities';
import {
  Check,
  ChevronDown,
  ChevronRight,
  GripVertical,
  Mic,
  Plus,
} from 'lucide-react';
import { brandColors } from '@/design';
import type {
  ActionItem,
  ActionItemStatus,
  DayFocus,
  TaskPlan,
  TodoProject,
  TodoSwimLane,
} from '../../types';
import {
  TODO_PROJECT_COLOR_TOKENS,
  TODO_SWIM_LANE_META,
} from '../../utils/todoSwimLanes';

/**
 * Bullets canvas — Workflowy-inspired outline projection over the same
 * `TaskPlan` / `DayFocus` documents that List + Kanban use.
 *
 * Page structure (Schedule grouping):
 *  - Lane heading per `TODO_SWIM_LANE_META` (Today / This week / Later).
 *  - Under the heading, a flat indented outline. Each `TaskPlan` becomes a
 *    top-level bullet (its `title` is the line text); each `ActionItem`
 *    becomes a child bullet. `parentItemId` allows arbitrary nesting inside
 *    one card.
 *  - Per-lane "+ new bullet" composer at the bottom of the lane creates a
 *    fresh lightweight `TaskPlan` so manual lines have a single source of
 *    truth shared with List / Kanban / Projects.
 *
 * Workflowy-style keyboard:
 *  - Enter             new sibling after the focused bullet
 *  - Shift+Enter       newline within the current bullet's text
 *  - Tab               indent under previous visible sibling at same level
 *  - Shift+Tab         outdent (clears parentItemId by one level)
 *  - Backspace empty   delete bullet, focus previous
 *  - Escape            revert in-progress edit
 *
 * Drag and drop (registered against the page-level `DndContext`):
 *  - Drag a bullet onto a lane heading dropzone -> move to that lane
 *  - Drag a bullet onto another bullet's "indent zone" -> set parentItemId
 *  - Drag a bullet onto a sibling's row -> reorder within parent
 *
 * The component intentionally renders **without** the boxed card chrome the
 * earlier draft used — the lane heading is the only structural framing, so
 * the surface reads as a flowing outline, not a stack of containers.
 */

export type BulletsRow =
  | { kind: 'task'; id: string; task: TaskPlan }
  | { kind: 'day_focus'; id: string; focus: DayFocus; task: TaskPlan };

export interface BulletsCanvasProps {
  /** Visible cards, already filtered for status / hideCompleted. */
  rows: BulletsRow[];
  /** Lane the row currently sits in (parent computes via swimLane heuristics). */
  laneForRow: (row: BulletsRow) => TodoSwimLane;
  /** All known projects, used to render the project chip on parent bullets. */
  projects: TodoProject[];

  // ─── Action-item mutations (mirror useTasks / useDayFocus) ───────────────
  onToggleItem: (
    row: BulletsRow,
    actionItemId: string,
    completed: boolean,
  ) => Promise<void> | void;
  onAddItem: (
    row: BulletsRow,
    text: string,
    options?: { parentItemId?: string | null; afterItemId?: string },
  ) => Promise<string | null | void> | string | null | void;
  onDeleteItem: (
    row: BulletsRow,
    actionItemId: string,
  ) => Promise<void> | void;
  onRenameItem: (
    row: BulletsRow,
    actionItemId: string,
    text: string,
  ) => Promise<void> | void;
  onSetItemParent: (
    row: BulletsRow,
    actionItemId: string,
    parentItemId: string | null,
  ) => Promise<void> | void;

  // ─── Top-level (TaskPlan / DayFocus) mutations ───────────────────────────
  onRenameRow: (row: BulletsRow, title: string) => Promise<void> | void;
  onDeleteRow: (row: BulletsRow) => Promise<void> | void;
  /** Creates a new lightweight TaskPlan for the given lane. Returns its id (if synchronous-enough) so we can focus it. */
  onCreateTopLevel: (lane: TodoSwimLane, text: string) => Promise<string | null> | string | null;
  /** Move a top-level row to a different lane (sets `swimLane`). */
  onMoveRowToLane: (row: BulletsRow, lane: TodoSwimLane) => Promise<void> | void;

  /** Optional: collapse persistence per lane shared with Schedule view. */
  laneCollapsed?: Record<TodoSwimLane, boolean>;
  toggleLaneCollapsed?: (lane: TodoSwimLane) => void;
}

// ─── Drag id helpers ────────────────────────────────────────────────────────
//
// Bullets register their own dnd-kit ids alongside the page-level draggables
// in TodosPage so the parsing in `handleWorkspaceDragEnd` stays simple. The
// outline owns its own prefix space so existing list/kanban handlers can
// keep using `ws-drag:` for cards.

const BULLET_DRAG_PREFIX = 'ws-bullet-drag:';
const BULLET_LANE_DROP_PREFIX = 'ws-bullet-lane:';
const BULLET_INDENT_DROP_PREFIX = 'ws-bullet-indent:';
const BULLET_REORDER_DROP_PREFIX = 'ws-bullet-reorder:';

export const BULLET_DRAG_IDS = {
  drag: BULLET_DRAG_PREFIX,
  lane: BULLET_LANE_DROP_PREFIX,
  indent: BULLET_INDENT_DROP_PREFIX,
  reorder: BULLET_REORDER_DROP_PREFIX,
};

/** Parsed bullet drag id: identifies which row + (optional) actionItem is being dragged. */
export type BulletDragHandle =
  | { kind: 'task'; rowId: string; actionItemId?: string }
  | { kind: 'day_focus'; rowId: string; actionItemId?: string };

export function parseBulletDragId(raw: string): BulletDragHandle | null {
  if (!raw.startsWith(BULLET_DRAG_PREFIX)) return null;
  const rest = raw.slice(BULLET_DRAG_PREFIX.length);
  // Format: `${kind}:${rowId}` for top-level, `${kind}:${rowId}:${itemId}` for child.
  const parts = rest.split(':');
  if (parts.length < 2) return null;
  const [kindRaw, rowId, ...tail] = parts;
  if (kindRaw !== 'task' && kindRaw !== 'focus') return null;
  const kind: 'task' | 'day_focus' = kindRaw === 'task' ? 'task' : 'day_focus';
  if (tail.length === 0) return { kind, rowId };
  return { kind, rowId, actionItemId: tail.join(':') };
}

export function parseBulletLaneDrop(raw: string): TodoSwimLane | null {
  if (!raw.startsWith(BULLET_LANE_DROP_PREFIX)) return null;
  const lane = raw.slice(BULLET_LANE_DROP_PREFIX.length);
  if (lane === 'today' || lane === 'this_week' || lane === 'later') return lane;
  return null;
}

/** Drop target meaning: nest the dragged bullet under this row's actionItem (or under the row's TaskPlan itself when actionItemId is omitted). */
export type BulletIndentDrop = {
  rowKind: 'task' | 'day_focus';
  rowId: string;
  /** When undefined, the drop indents under the top-level row (becomes a first child). */
  parentItemId?: string;
};

export function parseBulletIndentDrop(raw: string): BulletIndentDrop | null {
  if (!raw.startsWith(BULLET_INDENT_DROP_PREFIX)) return null;
  const rest = raw.slice(BULLET_INDENT_DROP_PREFIX.length);
  const parts = rest.split(':');
  if (parts.length < 2) return null;
  const [kindRaw, rowId, ...tail] = parts;
  if (kindRaw !== 'task' && kindRaw !== 'focus') return null;
  const rowKind: 'task' | 'day_focus' = kindRaw === 'task' ? 'task' : 'day_focus';
  if (tail.length === 0) return { rowKind, rowId };
  return { rowKind, rowId, parentItemId: tail.join(':') };
}

// ─── Item helpers ───────────────────────────────────────────────────────────

function getActionItemStatus(item: ActionItem): ActionItemStatus {
  if (item.status) return item.status;
  return item.completed ? 'done' : 'todo';
}

function isActionItemDone(item: ActionItem): boolean {
  return getActionItemStatus(item) === 'done';
}

interface OutlineItemNode {
  item: ActionItem;
  depth: number; // 1+ (top-level row is depth 0)
  children: OutlineItemNode[];
}

/** Build a tree of action items honouring `parentItemId`. Items whose declared parent doesn't exist fall back to top-level under the card. Stable by `order`. */
function buildItemTree(items: ActionItem[]): OutlineItemNode[] {
  const sorted = [...items].sort((a, b) => a.order - b.order);
  const byId = new Map(sorted.map((i) => [i.id, i] as const));
  const childrenByParent = new Map<string | null, ActionItem[]>();
  for (const it of sorted) {
    const parent = it.parentItemId && byId.has(it.parentItemId) ? it.parentItemId : null;
    const arr = childrenByParent.get(parent);
    if (arr) arr.push(it);
    else childrenByParent.set(parent, [it]);
  }
  function walk(parentId: string | null, depth: number): OutlineItemNode[] {
    const list = childrenByParent.get(parentId) ?? [];
    return list.map((item) => ({
      item,
      depth,
      children: walk(item.id, depth + 1),
    }));
  }
  return walk(null, 1);
}

/** Flatten a tree depth-first preserving display order. */
function flattenTree(tree: OutlineItemNode[]): OutlineItemNode[] {
  const out: OutlineItemNode[] = [];
  for (const node of tree) {
    out.push(node);
    out.push(...flattenTree(node.children));
  }
  return out;
}

// ─── Focus registry ─────────────────────────────────────────────────────────
//
// Bullet rows register a textarea ref under a string key so keyboard
// navigation can move focus between them (Enter -> next sibling, Backspace
// -> previous, Tab -> stay on current after indent). The key shape is:
//   top:${rowKey}                       (top-level row)
//   item:${rowKey}:${actionItemId}      (child item)
//   composer:${lane}                    (per-lane "+ new bullet" textarea)

type FocusKey = string;

interface FocusRegistry {
  register: (key: FocusKey, el: HTMLTextAreaElement | null) => void;
  focus: (key: FocusKey, opts?: { selectAll?: boolean; placeCaret?: 'end' | 'start' }) => void;
}

function useFocusRegistry(): FocusRegistry {
  const map = useRef<Map<FocusKey, HTMLTextAreaElement>>(new Map());
  const register = useCallback((key: FocusKey, el: HTMLTextAreaElement | null) => {
    if (el) map.current.set(key, el);
    else map.current.delete(key);
  }, []);
  const focus = useCallback((key: FocusKey, opts?: { selectAll?: boolean; placeCaret?: 'end' | 'start' }) => {
    // requestAnimationFrame so we land after the current keydown handler.
    window.requestAnimationFrame(() => {
      const el = map.current.get(key);
      if (!el) return;
      el.focus();
      if (opts?.selectAll) {
        el.select();
        return;
      }
      const len = el.value.length;
      if (opts?.placeCaret === 'start') el.setSelectionRange(0, 0);
      else el.setSelectionRange(len, len);
    });
  }, []);
  return { register, focus };
}

// ─── Helpers around row keys ───────────────────────────────────────────────

function rowKey(row: BulletsRow): string {
  return row.kind === 'task' ? `task:${row.id}` : `focus:${row.id}`;
}

function rowDragId(row: BulletsRow, actionItemId?: string): string {
  const kindShort = row.kind === 'task' ? 'task' : 'focus';
  if (!actionItemId) return `${BULLET_DRAG_PREFIX}${kindShort}:${row.id}`;
  return `${BULLET_DRAG_PREFIX}${kindShort}:${row.id}:${actionItemId}`;
}

function rowIndentDropId(row: BulletsRow, parentItemId?: string): string {
  const kindShort = row.kind === 'task' ? 'task' : 'focus';
  if (!parentItemId) return `${BULLET_INDENT_DROP_PREFIX}${kindShort}:${row.id}`;
  return `${BULLET_INDENT_DROP_PREFIX}${kindShort}:${row.id}:${parentItemId}`;
}

// ─── Bullet line ────────────────────────────────────────────────────────────

interface BulletLineProps {
  text: string;
  draftKey: string;
  /** Stable focus key used by the focus registry. */
  focusKey: FocusKey;
  registerFocus: (key: FocusKey, el: HTMLTextAreaElement | null) => void;
  /** Indent depth for visual offset. 0 = top-level row, 1+ = nested item. */
  depth: number;
  /** Whether to render a checkbox. Top-level rows currently render without one (display only via the bullet dot color). */
  showCheckbox: boolean;
  /** Done state when checkbox is enabled. */
  done?: boolean;
  status?: ActionItemStatus;
  onToggleDone?: (next: boolean) => void;
  /** Persist text (rename or delete-if-empty depending on caller). */
  onCommit: (next: string) => void;
  /** Caller-provided revert: when escape is pressed we restore to original text. */
  onRevert?: () => void;
  // Keyboard intents:
  onEnter: () => void;
  onTab: () => void;
  onShiftTab: () => void;
  onBackspaceEmpty: () => void;
  onArrowUp?: () => void;
  onArrowDown?: () => void;

  /** Render a drag handle on hover with the given dnd-kit id. */
  dragId: string;
  /** Visual extras: collapse caret, provenance chip, project chip, count badge. */
  leadingControl?: React.ReactNode;
  trailing?: React.ReactNode;
  // Bullet-dot styling tweaks:
  bulletColor?: string;
  bulletFilled?: boolean;
  /** When true the line is a TaskPlan/DayFocus title — slightly heavier weight. */
  isBranchHead?: boolean;
  /** Extra child slot rendered under the bullet (e.g. the "from session" chip on rows). */
  belowText?: React.ReactNode;
  /** Composer-mode hides the drag handle and the toggle. */
  composer?: boolean;
}

/** Indent step in pixels — kept conservative so a 6-level outline still fits comfortably on phone widths. */
const INDENT_STEP_PX = 18;

const BulletLine = forwardRef<HTMLDivElement, BulletLineProps>(function BulletLine(
  props,
  outerRef,
) {
  const {
    text,
    draftKey,
    focusKey,
    registerFocus,
    depth,
    showCheckbox,
    done,
    status,
    onToggleDone,
    onCommit,
    onRevert,
    onEnter,
    onTab,
    onShiftTab,
    onBackspaceEmpty,
    onArrowUp,
    onArrowDown,
    dragId,
    leadingControl,
    trailing,
    bulletColor,
    bulletFilled,
    isBranchHead,
    belowText,
    composer,
  } = props;

  const [draft, setDraft] = useState(text);
  const inputRef = useRef<HTMLTextAreaElement | null>(null);
  const [hover, setHover] = useState(false);

  // Reset draft when the underlying text changes from outside (e.g. another tab edit).
  useEffect(() => {
    setDraft(text);
  }, [draftKey, text]);

  // Auto-grow textarea height to its content.
  useEffect(() => {
    const el = inputRef.current;
    if (!el) return;
    el.style.height = '0px';
    el.style.height = `${el.scrollHeight}px`;
  }, [draft]);

  const setRefs = useCallback(
    (el: HTMLTextAreaElement | null) => {
      inputRef.current = el;
      registerFocus(focusKey, el);
    },
    [focusKey, registerFocus],
  );

  const commitIfChanged = () => {
    if (draft === text) return;
    onCommit(draft);
  };

  const handleKey = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    // Shift+Enter -> insert literal newline (default behaviour).
    if (e.key === 'Enter' && !e.shiftKey && !e.altKey && !e.metaKey) {
      e.preventDefault();
      commitIfChanged();
      onEnter();
      return;
    }
    if (e.key === 'Tab') {
      e.preventDefault();
      commitIfChanged();
      if (e.shiftKey) onShiftTab();
      else onTab();
      return;
    }
    if (e.key === 'Backspace' && draft.length === 0) {
      e.preventDefault();
      onBackspaceEmpty();
      return;
    }
    if (e.key === 'Escape') {
      e.preventDefault();
      setDraft(text);
      onRevert?.();
      inputRef.current?.blur();
      return;
    }
    if (e.key === 'ArrowUp' && onArrowUp) {
      const el = inputRef.current;
      if (el && el.selectionStart === 0 && el.selectionEnd === 0) {
        e.preventDefault();
        commitIfChanged();
        onArrowUp();
      }
      return;
    }
    if (e.key === 'ArrowDown' && onArrowDown) {
      const el = inputRef.current;
      if (el && el.selectionStart === draft.length && el.selectionEnd === draft.length) {
        e.preventDefault();
        commitIfChanged();
        onArrowDown();
      }
      return;
    }
  };

  // Drag handle for this bullet (registers with the page DndContext).
  const draggable = useDraggable({ id: dragId });
  const dragStyle = draggable.transform
    ? {
        transform: CSS.Transform.toString(draggable.transform),
        zIndex: draggable.isDragging ? 20 : undefined,
      }
    : undefined;

  const radioStyle = !showCheckbox
    ? undefined
    : done
    ? {
        border: 'none',
        background: 'var(--color-success)',
      }
    : status === 'in_progress'
    ? {
        border: `2px solid ${brandColors.lightBlue}`,
        background: 'transparent',
        boxShadow: `0 0 0 2px ${brandColors.lightBlue}22`,
      }
    : {
        border: '2px solid var(--color-text-secondary)',
        background: 'transparent',
      };

  const lineOpacity = draggable.isDragging ? 0.55 : 1;
  const indentPx = depth * INDENT_STEP_PX;

  return (
    <div
      ref={(el) => {
        // Combine outerRef + draggable.setNodeRef.
        if (typeof outerRef === 'function') outerRef(el);
        else if (outerRef) (outerRef as React.MutableRefObject<HTMLDivElement | null>).current = el;
        draggable.setNodeRef(el);
      }}
      style={{ ...dragStyle, opacity: lineOpacity, paddingLeft: indentPx }}
      className="group flex items-start gap-2 -mx-2 px-2 py-0.5 rounded-md transition-colors hover:bg-[var(--color-surface)]"
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
    >
      {!composer ? (
        <button
          type="button"
          aria-label="Drag bullet"
          className="shrink-0 mt-1.5 -ml-1 p-0.5 rounded text-[var(--color-text-muted)] opacity-0 group-hover:opacity-100 focus-visible:opacity-100 transition-opacity touch-none cursor-grab active:cursor-grabbing"
          {...draggable.listeners}
          {...draggable.attributes}
        >
          <GripVertical size={12} />
        </button>
      ) : (
        <span aria-hidden className="shrink-0 w-3.5" />
      )}

      {leadingControl ? <div className="shrink-0">{leadingControl}</div> : null}

      {showCheckbox ? (
        <button
          type="button"
          onClick={() => onToggleDone?.(!done)}
          className="shrink-0 mt-1.5 w-4 h-4 rounded-full flex items-center justify-center"
          style={radioStyle}
          aria-label={done ? 'Mark as not done' : 'Mark as done'}
        >
          {done ? <Check size={10} color="white" strokeWidth={3} /> : null}
        </button>
      ) : null}

      <span
        aria-hidden
        className={`shrink-0 mt-2 rounded-full ${isBranchHead ? 'w-2 h-2' : 'w-1.5 h-1.5'}`}
        style={{
          background: bulletFilled
            ? bulletColor ?? (hover ? 'var(--color-text-secondary)' : 'var(--color-text-muted)')
            : 'transparent',
          border: bulletFilled ? 'none' : `1.5px solid ${bulletColor ?? 'var(--color-text-muted)'}`,
        }}
      />

      <div className="flex-1 min-w-0">
        <textarea
          ref={setRefs}
          rows={1}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={commitIfChanged}
          onKeyDown={handleKey}
          placeholder={composer ? 'New bullet — Enter to add' : 'Empty bullet — Backspace to delete'}
          className={`w-full bg-transparent resize-none border-0 outline-none text-sm leading-snug py-0.5 placeholder:text-[var(--color-text-muted)] ${
            done
              ? 'text-[var(--color-text-muted)] line-through'
              : isBranchHead
              ? 'text-[var(--color-text-primary)] font-semibold'
              : 'text-[var(--color-text-primary)]'
          }`}
          style={{ fontFamily: 'inherit' }}
        />
        {belowText}
      </div>

      {trailing ? <div className="shrink-0">{trailing}</div> : null}
    </div>
  );
});

// ─── Indent + reorder drop zones (thin overlays) ───────────────────────────

function IndentDropOverlay({
  dropId,
  active,
}: {
  dropId: string;
  active: boolean;
}) {
  const { setNodeRef, isOver } = useDroppable({ id: dropId });
  if (!active && !isOver) {
    // Always render the node so dnd-kit can register it; visibility is opacity-driven.
    return <div ref={setNodeRef} aria-hidden style={{ height: 0 }} />;
  }
  const styleVars: CSSProperties = {
    background: isOver ? `${brandColors.teal}33` : `${brandColors.teal}10`,
    border: `1px dashed ${brandColors.teal}88`,
    height: 4,
    borderRadius: 6,
    transition: 'background 120ms, border-color 120ms',
  };
  return (
    <div ref={setNodeRef} aria-hidden className="-my-0.5 mx-2" style={styleVars} />
  );
}

function LaneHeadingDropZone({
  lane,
  children,
  active,
}: {
  lane: TodoSwimLane;
  children: React.ReactNode;
  active: boolean;
}) {
  const { setNodeRef, isOver } = useDroppable({ id: `${BULLET_LANE_DROP_PREFIX}${lane}` });
  // Visual stack:
  //  - default: no chrome
  //  - drag in progress (active): subtle dashed outline so the lane reads as
  //    a valid drop target without being too loud
  //  - drag is over this lane: stronger teal outline + soft tint
  let outline: string | undefined;
  let background: string | undefined;
  if (active && isOver) {
    outline = `2px dashed ${brandColors.teal}`;
    background = `${brandColors.teal}10`;
  } else if (active) {
    outline = `1px dashed ${brandColors.teal}55`;
    background = `${brandColors.teal}05`;
  }
  return (
    <div
      ref={setNodeRef}
      style={
        outline
          ? {
              outline,
              outlineOffset: 4,
              borderRadius: 12,
              background,
              transition: 'outline-color 120ms, background 120ms',
            }
          : undefined
      }
    >
      {children}
    </div>
  );
}

// ─── Per-lane composer (top-level "+ new bullet") ──────────────────────────

interface LaneComposerProps {
  lane: TodoSwimLane;
  registerFocus: (key: FocusKey, el: HTMLTextAreaElement | null) => void;
  onCreate: (text: string) => Promise<string | null> | string | null;
  onCreatedFocusFirstChild?: (newRowId: string) => void;
}

const LaneComposer = forwardRef<HTMLTextAreaElement, LaneComposerProps>(function LaneComposer(
  { lane, registerFocus, onCreate },
  fwdRef,
) {
  const [draft, setDraft] = useState('');
  const inputRef = useRef<HTMLTextAreaElement | null>(null);
  const focusKey: FocusKey = `composer:${lane}`;

  useImperativeHandle(fwdRef, () => inputRef.current as HTMLTextAreaElement);

  const setRefs = useCallback(
    (el: HTMLTextAreaElement | null) => {
      inputRef.current = el;
      registerFocus(focusKey, el);
    },
    [focusKey, registerFocus],
  );

  useEffect(() => {
    const el = inputRef.current;
    if (!el) return;
    el.style.height = '0px';
    el.style.height = `${el.scrollHeight}px`;
  }, [draft]);

  const submit = async () => {
    const trimmed = draft.trim();
    if (!trimmed) return;
    setDraft('');
    await onCreate(trimmed);
    window.requestAnimationFrame(() => inputRef.current?.focus());
  };

  return (
    <div
      className="group flex items-start gap-2 -mx-2 px-2 py-0.5 rounded-md hover:bg-[var(--color-surface)] transition-colors"
      style={{ paddingLeft: 0 }}
    >
      <span aria-hidden className="shrink-0 w-3.5" />
      <span
        aria-hidden
        className="shrink-0 mt-2 w-1.5 h-1.5 rounded-full"
        style={{
          border: '1.5px dashed var(--color-text-muted)',
          background: 'transparent',
        }}
      />
      <Plus size={12} className="shrink-0 mt-1.5 text-[var(--color-text-muted)] opacity-60" />
      <textarea
        ref={setRefs}
        rows={1}
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            void submit();
          } else if (e.key === 'Escape') {
            e.preventDefault();
            setDraft('');
            inputRef.current?.blur();
          }
        }}
        onBlur={() => {
          const trimmed = draft.trim();
          if (trimmed) {
            setDraft('');
            void onCreate(trimmed);
          }
        }}
        placeholder="New bullet — Enter to add"
        className="flex-1 min-w-0 bg-transparent resize-none border-0 outline-none text-sm leading-snug py-0.5 text-[var(--color-text-primary)] placeholder:text-[var(--color-text-muted)]"
        style={{ fontFamily: 'inherit' }}
      />
    </div>
  );
});

// ─── Main canvas ────────────────────────────────────────────────────────────

export function BulletsCanvas({
  rows,
  laneForRow,
  projects,
  onToggleItem,
  onAddItem,
  onDeleteItem,
  onRenameItem,
  onSetItemParent,
  onRenameRow,
  onDeleteRow,
  onCreateTopLevel,
  onMoveRowToLane: _onMoveRowToLane,
  laneCollapsed,
  toggleLaneCollapsed,
}: BulletsCanvasProps) {
  const projectsById = useMemo(() => {
    const map = new Map<string, TodoProject>();
    for (const p of projects) map.set(p.id, p);
    return map;
  }, [projects]);

  /** Per-row collapse state (independent of lane collapse). */
  const [rowCollapsed, setRowCollapsed] = useState<Record<string, boolean>>({});
  const toggleRowCollapsed = useCallback((key: string) => {
    setRowCollapsed((prev) => ({ ...prev, [key]: !prev[key] }));
  }, []);

  /** Track whether any bullet is currently being dragged so we light up indent zones. */
  const [dragActive, setDragActive] = useState(false);
  useDndMonitor({
    onDragStart: (e) => {
      const id = String(e.active.id);
      if (id.startsWith(BULLET_DRAG_PREFIX)) setDragActive(true);
    },
    onDragCancel: () => setDragActive(false),
    onDragEnd: () => setDragActive(false),
  });

  const focusReg = useFocusRegistry();

  const rowsByLane = useMemo(() => {
    const map: Record<TodoSwimLane, BulletsRow[]> = { today: [], this_week: [], later: [] };
    for (const row of rows) {
      const lane = laneForRow(row);
      map[lane].push(row);
    }
    return map;
  }, [rows, laneForRow]);

  return (
    <div className="flex flex-col gap-7">
      {TODO_SWIM_LANE_META.map((laneMeta) => {
        const collapsed = laneCollapsed?.[laneMeta.id] ?? false;
        const laneRows = rowsByLane[laneMeta.id];
        const count = laneRows.length;

        return (
          <LaneHeadingDropZone key={laneMeta.id} lane={laneMeta.id} active={dragActive}>
            <section
              id={`ws-lane-${laneMeta.id}`}
              className="flex flex-col gap-1 scroll-mt-24"
            >
              <button
                type="button"
                onClick={() => toggleLaneCollapsed?.(laneMeta.id)}
                className="flex items-center gap-2 text-left rounded-lg p-1 -ml-1 hover:bg-[var(--color-surface)] transition-colors"
                aria-expanded={!collapsed}
              >
                {collapsed ? (
                  <ChevronRight size={16} className="text-[var(--color-text-muted)]" />
                ) : (
                  <ChevronDown size={16} className="text-[var(--color-text-muted)]" />
                )}
                <h2 className="text-sm font-semibold text-[var(--color-text-primary)]">
                  {laneMeta.label}
                </h2>
                <span className="text-[11px] text-[var(--color-text-muted)]">
                  {count === 0 ? 'nothing planned' : `${count} bullet${count === 1 ? '' : 's'}`}
                </span>
              </button>

              {!collapsed ? (
                <div className="flex flex-col">
                  {count === 0 ? (
                    <p className="text-sm text-[var(--color-text-muted)] py-1 px-2 pl-7">
                      No bullets in {laneMeta.label.toLowerCase()} yet — add one below or capture from a session.
                    </p>
                  ) : (
                    laneRows.map((row) => (
                      <RowOutline
                        key={rowKey(row)}
                        row={row}
                        project={
                          row.task.projectId ? projectsById.get(row.task.projectId) ?? null : null
                        }
                        collapsed={Boolean(rowCollapsed[rowKey(row)])}
                        onToggleCollapse={() => toggleRowCollapsed(rowKey(row))}
                        focusReg={focusReg}
                        dragActive={dragActive}
                        onToggleItem={onToggleItem}
                        onAddItem={onAddItem}
                        onDeleteItem={onDeleteItem}
                        onRenameItem={onRenameItem}
                        onSetItemParent={onSetItemParent}
                        onRenameRow={onRenameRow}
                        onDeleteRow={onDeleteRow}
                      />
                    ))
                  )}
                  <LaneComposer
                    lane={laneMeta.id}
                    registerFocus={focusReg.register}
                    onCreate={async (text) => onCreateTopLevel(laneMeta.id, text)}
                  />
                </div>
              ) : null}
            </section>
          </LaneHeadingDropZone>
        );
      })}

      <p className="text-[11px] text-[var(--color-text-muted)] px-2 pt-1">
        Bullets share the same data as List &amp; Kanban. Tab / Shift+Tab to nest, Enter for next bullet, drag to a lane to reschedule.
      </p>
    </div>
  );
}

// ─── Per-row outline ────────────────────────────────────────────────────────

interface RowOutlineProps {
  row: BulletsRow;
  project: TodoProject | null;
  collapsed: boolean;
  onToggleCollapse: () => void;
  focusReg: FocusRegistry;
  dragActive: boolean;
  onToggleItem: BulletsCanvasProps['onToggleItem'];
  onAddItem: BulletsCanvasProps['onAddItem'];
  onDeleteItem: BulletsCanvasProps['onDeleteItem'];
  onRenameItem: BulletsCanvasProps['onRenameItem'];
  onSetItemParent: BulletsCanvasProps['onSetItemParent'];
  onRenameRow: BulletsCanvasProps['onRenameRow'];
  onDeleteRow: BulletsCanvasProps['onDeleteRow'];
}

function RowOutline({
  row,
  project,
  collapsed,
  onToggleCollapse,
  focusReg,
  dragActive,
  onToggleItem,
  onAddItem,
  onDeleteItem,
  onRenameItem,
  onSetItemParent,
  onRenameRow,
  onDeleteRow,
}: RowOutlineProps) {
  const items = useMemo(
    () =>
      [...row.task.actionItems]
        .map((i) => ({ ...i, status: getActionItemStatus(i), completed: isActionItemDone(i) }))
        .sort((a, b) => a.order - b.order),
    [row.task.actionItems],
  );

  const tree = useMemo(() => buildItemTree(items), [items]);
  const flattened = useMemo(() => flattenTree(tree), [tree]);

  const total = items.length;
  const done = items.filter((i) => isActionItemDone(i)).length;
  const fullyDone = total > 0 && done === total;
  const isDayFocus = row.kind === 'day_focus';

  // Build a quick lookup so we can compute "previous sibling at same level" for Tab indenting.
  const byId = useMemo(() => new Map(items.map((i) => [i.id, i] as const)), [items]);

  const focusKeyFor = (itemId?: string): FocusKey =>
    itemId ? `item:${rowKey(row)}:${itemId}` : `top:${rowKey(row)}`;

  // ── Top-level row keyboard intents ───────────────────────────────────────
  const handleTopEnter = async () => {
    // Insert a new sibling action item at the top of this row (after the title).
    const newId = await onAddItem(row, '', { parentItemId: null });
    if (typeof newId === 'string') {
      focusReg.focus(focusKeyFor(newId));
    } else if (items.length > 0) {
      focusReg.focus(focusKeyFor(items[0].id));
    }
  };
  const handleTopBackspace = () => {
    if (items.length > 0) return; // Don't accidentally delete a row that still has children.
    void onDeleteRow(row);
  };

  // ── Item keyboard intents ────────────────────────────────────────────────
  const handleItemEnter = async (itemId: string) => {
    const item = byId.get(itemId);
    const newId = await onAddItem(row, '', {
      parentItemId: item?.parentItemId ?? null,
      afterItemId: itemId,
    });
    if (typeof newId === 'string') {
      focusReg.focus(focusKeyFor(newId));
    }
  };

  const handleItemTab = async (itemId: string) => {
    // Indent under the previous sibling at the same level.
    const item = byId.get(itemId);
    if (!item) return;
    const myParent = item.parentItemId ?? null;
    // Walk the in-order list and find the closest previous item with the same parent.
    let prevSibling: ActionItem | null = null;
    for (const node of flattened) {
      if (node.item.id === itemId) break;
      const np = node.item.parentItemId ?? null;
      if (np === myParent) prevSibling = node.item;
    }
    if (!prevSibling) return; // Nothing to indent under.
    await onSetItemParent(row, itemId, prevSibling.id);
    focusReg.focus(focusKeyFor(itemId));
  };

  const handleItemShiftTab = async (itemId: string) => {
    const item = byId.get(itemId);
    if (!item || !item.parentItemId) return; // Already top-level under the row.
    const parent = byId.get(item.parentItemId);
    const grandparent = parent?.parentItemId ?? null;
    await onSetItemParent(row, itemId, grandparent);
    focusReg.focus(focusKeyFor(itemId));
  };

  const handleItemBackspaceEmpty = (itemId: string) => {
    // Focus previous bullet first, then delete.
    const idx = flattened.findIndex((n) => n.item.id === itemId);
    const prevKey: FocusKey =
      idx > 0 ? focusKeyFor(flattened[idx - 1].item.id) : focusKeyFor(undefined); // fall back to top-level row
    void onDeleteItem(row, itemId);
    focusReg.focus(prevKey);
  };

  const projectTokens = project?.colorKey ? TODO_PROJECT_COLOR_TOKENS[project.colorKey] : null;

  return (
    <div className="flex flex-col">
      {/* Top-level row (TaskPlan / DayFocus title as a bullet line). */}
      <BulletLine
        text={row.task.title}
        draftKey={`${rowKey(row)}::${row.task.title}`}
        focusKey={focusKeyFor(undefined)}
        registerFocus={focusReg.register}
        depth={0}
        showCheckbox={false}
        bulletColor={
          fullyDone
            ? 'var(--color-success)'
            : isDayFocus
            ? brandColors.teal
            : 'var(--color-text-secondary)'
        }
        bulletFilled
        isBranchHead
        leadingControl={
          total > 0 ? (
            <button
              type="button"
              onClick={onToggleCollapse}
              className="mt-0.5 p-0.5 rounded text-[var(--color-text-muted)] hover:text-[var(--color-text-secondary)] hover:bg-[var(--color-surface-elevated)] transition-colors"
              aria-label={collapsed ? 'Expand sub-bullets' : 'Collapse sub-bullets'}
              aria-expanded={!collapsed}
            >
              {collapsed ? <ChevronRight size={14} /> : <ChevronDown size={14} />}
            </button>
          ) : null
        }
        trailing={
          <div className="flex items-center gap-2">
            {total > 0 ? (
              <span className="text-[11px] text-[var(--color-text-muted)]">{done}/{total}</span>
            ) : null}
            {projectTokens ? (
              <span
                className="inline-flex items-center gap-1 text-[10px] font-medium px-1.5 py-0.5 rounded-full"
                style={{ background: projectTokens.tint, color: projectTokens.fg }}
              >
                <span
                  aria-hidden
                  className="w-1.5 h-1.5 rounded-full"
                  style={{ background: projectTokens.fg }}
                />
                {project?.name}
              </span>
            ) : null}
          </div>
        }
        belowText={
          row.task.sessionId ? (
            <span className="inline-flex items-center gap-1 text-[10px] text-[var(--color-text-muted)] mt-0.5">
              <Mic size={10} /> from session
            </span>
          ) : null
        }
        dragId={rowDragId(row)}
        onEnter={handleTopEnter}
        onTab={() => {
          /* No-op for now: Tab on a top-level row across-cards would require
             promoting/demoting between TaskPlans. Cross-card moves are
             deferred per the existing PRD. */
        }}
        onShiftTab={() => {
          /* No-op: top-level rows are already at the lane root. */
        }}
        onBackspaceEmpty={handleTopBackspace}
        onCommit={(next) => {
          const trimmed = next.trim();
          if (!trimmed) return; // Don't allow blanking the title.
          if (trimmed === row.task.title) return;
          void onRenameRow(row, trimmed);
        }}
      />

      {/* Indent dropzone right under the top-level row -> drop here to nest as first child. */}
      <IndentDropOverlay dropId={rowIndentDropId(row)} active={dragActive} />

      {!collapsed
        ? renderItemNodes(tree, {
            row,
            focusReg,
            focusKeyFor,
            dragActive,
            onToggleItem,
            onRenameItem,
            onItemEnter: handleItemEnter,
            onItemTab: handleItemTab,
            onItemShiftTab: handleItemShiftTab,
            onItemBackspaceEmpty: handleItemBackspaceEmpty,
          })
        : null}
    </div>
  );
}

// ─── Recursive item tree renderer ───────────────────────────────────────────

interface RenderCtx {
  row: BulletsRow;
  focusReg: FocusRegistry;
  focusKeyFor: (itemId?: string) => FocusKey;
  dragActive: boolean;
  onToggleItem: BulletsCanvasProps['onToggleItem'];
  onRenameItem: BulletsCanvasProps['onRenameItem'];
  onItemEnter: (itemId: string) => void | Promise<void>;
  onItemTab: (itemId: string) => void | Promise<void>;
  onItemShiftTab: (itemId: string) => void | Promise<void>;
  onItemBackspaceEmpty: (itemId: string) => void;
}

function renderItemNodes(nodes: OutlineItemNode[], ctx: RenderCtx): React.ReactNode {
  return nodes.map((node) => {
    const { item, depth, children } = node;
    const done = isActionItemDone(item);
    const status = getActionItemStatus(item);

    return (
      <div key={item.id} className="flex flex-col">
        <BulletLine
          text={item.text}
          draftKey={`${rowKey(ctx.row)}::${item.id}::${item.text}`}
          focusKey={ctx.focusKeyFor(item.id)}
          registerFocus={ctx.focusReg.register}
          depth={depth}
          showCheckbox
          done={done}
          status={status}
          onToggleDone={(next) => void ctx.onToggleItem(ctx.row, item.id, next)}
          onCommit={(next) => {
            const trimmed = next.trim();
            if (trimmed === item.text) return;
            void ctx.onRenameItem(ctx.row, item.id, trimmed);
          }}
          onEnter={() => void ctx.onItemEnter(item.id)}
          onTab={() => void ctx.onItemTab(item.id)}
          onShiftTab={() => void ctx.onItemShiftTab(item.id)}
          onBackspaceEmpty={() => ctx.onItemBackspaceEmpty(item.id)}
          dragId={rowDragId(ctx.row, item.id)}
          bulletColor={done ? 'var(--color-success)' : 'var(--color-text-muted)'}
          bulletFilled={done || depth === 1}
        />
        {/* Nest-under-this-item drop zone. */}
        <IndentDropOverlay
          dropId={rowIndentDropId(ctx.row, item.id)}
          active={ctx.dragActive}
        />
        {children.length > 0 ? renderItemNodes(children, ctx) : null}
      </div>
    );
  });
}
